import i18next from 'i18next';
import { Dispatch } from 'redux';
import api from '../../../api';
import { Toaster } from '../../shared/component/toaster';
import { validateEntryForm } from '../../validation';
import { DocumentType } from '../type';
import { DocumentReducerType } from './documents-reducer';
import { FileUploadStatusEnum } from '../../../enums';
import _ from 'lodash';

export enum DocumentActionsEnum {
  DOCUMENT_CREATE = 'DOCUMENT_CREATE',
  DOCUMENT_DELETE = 'DOCUMENT_DELETE',
  DOCUMENT_RESTORE = 'DOCUMENT_RESTORE',
  DOCUMENT_UPDATE = 'DOCUMENT_UPDATE',
  DOCUMENT_UPDATE_COMMENT = 'DOCUMENT_UPDATE_COMMENT',
  DOCUMENT_FETCH = 'DOCUMENT_FETCH',
  DOCUMENT_TRANSFER = 'DOCUMENT_TRANSFER',
  DOCUMENT_EUGBR_FETCH = 'DOCUMENT_EUGBR_FETCH',
  DOCUMENT_EUGBR_COMMENT_FETCH = 'DOCUMENT_EUGBR_COMMENT_FETCH',
  DOCUMENT_APPROVED_FETCH = 'DOCUMENT_APPROVED_FETCH',
  DOCUMENT_DOCUMENT_EUGBR_REFRESH = 'DOCUMENT_DOCUMENT_EUGBR_REFRESH',
  DOCUMENT_DOCUMENT_EUGBR_COMMENT_REFRESH = 'DOCUMENT_DOCUMENT_EUGBR_COMMENT_REFRESH',
  DOCUMENT_DOCUMENT_EUGBR_ADDITIONAL_REFRESH = 'DOCUMENT_DOCUMENT_EUGBR_ADDITIONAL_REFRESH',
  DOCUMENT_APPROVED_REFRESH = 'DOCUMENT_APPROVED_REFRESH',
  DOCUMENT_INCORPORATED_FETCH = 'DOCUMENT_INCORPORATED_FETCH',
  DOCUMENT_INCORPORATED_REFRESH = 'DOCUMENT_INCORPORATED_REFRESH',
  DOCUMENT_ADDITIONAL_FETCH = 'DOCUMENT_ADDITIONAL_FETCH',
  DOCUMENT_EUGBR_ADDITIONAL_FETCH = 'DOCUMENT_EUGBR_ADDITIONAL_FETCH',
  DOCUMENT_ADDITIONAL_REFRESH = 'DOCUMENT_ADDITIONAL_REFRESH',
  DOCUMENT_EUGBR_ADDITIONAL_REFRESH = 'DOCUMENT_ADDITIONAL_REFRESH',
  DOCUMENT_CSSF_FETCH = 'DOCUMENT_CSSF_FETCH',
  DOCUMENT_TRANSLATION_FETCH = 'DOCUMENT_TRANSLATION_FETCH',
  DOCUMENT_TRANSLATION_REFRESH = 'DOCUMENT_TRANSLATION_REFRESH',
  RESET_STATE = 'RESET_STATE',
}
type LoadingPayloadType = { isLoading: boolean };
export type DocumentDispatchType =
  | { type: DocumentActionsEnum.DOCUMENT_CREATE; payload: { isLoading: boolean; values: DocumentType[] } }
  | { type: DocumentActionsEnum.DOCUMENT_UPDATE; payload: { isLoading: boolean; values?: DocumentType[] } }
  | { type: DocumentActionsEnum.DOCUMENT_UPDATE_COMMENT; payload: { isLoading: boolean; values: DocumentType[] } }
  | { type: DocumentActionsEnum.DOCUMENT_DELETE; payload: { isLoading: boolean } }
  | { type: DocumentActionsEnum.DOCUMENT_RESTORE; payload: { isLoading: boolean; values?: DocumentType } }
  // TRANSFER
  | { type: DocumentActionsEnum.DOCUMENT_TRANSFER; payload: LoadingPayloadType } // FETCH
  | { type: DocumentActionsEnum.DOCUMENT_FETCH; payload: { values: DocumentType | undefined } }
  | { type: DocumentActionsEnum.DOCUMENT_EUGBR_FETCH; payload: DocumentReducerType['eugbrs'] }
  | { type: DocumentActionsEnum.DOCUMENT_EUGBR_COMMENT_FETCH; payload: DocumentReducerType['eugbrComments'] }
  | { type: DocumentActionsEnum.DOCUMENT_APPROVED_FETCH; payload: DocumentReducerType['approved'] }
  | { type: DocumentActionsEnum.DOCUMENT_INCORPORATED_FETCH; payload: DocumentReducerType['incorporated'] }
  | { type: DocumentActionsEnum.DOCUMENT_ADDITIONAL_FETCH; payload: DocumentReducerType['additional'] }
  | { type: DocumentActionsEnum.DOCUMENT_EUGBR_ADDITIONAL_FETCH; payload: DocumentReducerType['eugbrAdditional'] }
  | { type: DocumentActionsEnum.DOCUMENT_CSSF_FETCH; payload: DocumentReducerType['cssf'] }
  | { type: DocumentActionsEnum.DOCUMENT_TRANSLATION_FETCH; payload: DocumentReducerType['translation'] }
  // REFRESH
  | { type: DocumentActionsEnum.DOCUMENT_TRANSLATION_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_INCORPORATED_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_APPROVED_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_COMMENT_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_ADDITIONAL_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_ADDITIONAL_REFRESH }
  | { type: DocumentActionsEnum.DOCUMENT_EUGBR_ADDITIONAL_REFRESH }
  // RESET
  | { type: DocumentActionsEnum.RESET_STATE };

export async function create(
  entryForm: any,
  formValues: DocumentType,
  dataFile: Blob,
  callback: (documentId: string) => void,
  dispatch: Dispatch<DocumentDispatchType>
) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_CREATE, payload: { isLoading: true, values: [] } });

    const document = new FormData();
    const formValuesStr = JSON.stringify(formValues);
    document.append('document', new Blob([formValuesStr], { type: 'application/json' }));
    document.append('file', dataFile, formValues.name);

    const resp = await api.documents.create(entryForm.id, document);

    // @ts-ignore
    dispatch({ type: DocumentActionsEnum.DOCUMENT_CREATE, payload: { isLoading: false, values: resp.data } });
    Toaster.success(i18next.t('notifications.success.update'));

    dispatch(validateEntryForm(entryForm) as any);

    callback(resp.data.id);
  } catch (error: any) {
    if (error.response?.status === 422) {
      Toaster.error(i18next.t('upload.invalidPdfUpload'));
    }
    dispatch({ type: DocumentActionsEnum.DOCUMENT_CREATE, payload: { isLoading: false, values: [] } });
  }
}

export async function deleteDocument(
  { entryFormId, id }: any,
  entryForm: any,
  fireFetch: () => void,
  dispatch: Dispatch<DocumentDispatchType>
) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_DELETE, payload: { isLoading: true } });

    await api.documents.delete(entryFormId, id);

    Toaster.success(i18next.t('notifications.success.delete'));

    fireFetch();

    dispatch({ type: DocumentActionsEnum.DOCUMENT_DELETE, payload: { isLoading: false } });

    dispatch(validateEntryForm(entryForm) as any);
  } catch (error) {
    console.log(error);
    dispatch({ type: DocumentActionsEnum.DOCUMENT_DELETE, payload: { isLoading: false } });
  }
}

export async function restoreDocumentAction(
  entryFormId: string,
  id: string,
  fireFetch: () => void,
  dispatch: Dispatch<DocumentDispatchType>
) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_RESTORE, payload: { isLoading: true } });

    const resp = await api.documents.restore(entryFormId, id);

    Toaster.success(i18next.t('notifications.success.restore'));

    dispatch({ type: DocumentActionsEnum.DOCUMENT_RESTORE, payload: { isLoading: false, values: resp.data } });
  } catch (error) {
    console.log(error);
    dispatch({ type: DocumentActionsEnum.DOCUMENT_RESTORE, payload: { isLoading: false } });
  }
}

export async function editDocumentAction(
  entryFormId: string,
  id: string,
  document: DocumentType,
  fireFetch: () => void,
  dispatch: Dispatch<DocumentDispatchType>
) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE, payload: { isLoading: true } });

    await api.documents.update(entryFormId, id, document);

    Toaster.success(i18next.t('notifications.success.update'));

    fireFetch();

    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE, payload: { isLoading: false } });
  } catch (error) {
    console.log(error);
    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE, payload: { isLoading: false } });
  }
}

export async function updateComment(
  entryFormId: string,
  id: string,
  publicComment: string,
  callback: () => void,
  dispatch: Dispatch<DocumentDispatchType>
) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE_COMMENT, payload: { isLoading: true, values: [] } });

    const resp = await api.documents.updateComment(entryFormId, id, publicComment);

    // @ts-ignore
    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE_COMMENT, payload: { isLoading: false, values: resp.data } });
    Toaster.success(i18next.t('notifications.success.update'));

    callback();
  } catch (error) {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_UPDATE_COMMENT, payload: { isLoading: false, values: [] } });
  }
}

export async function findOne(entryForm: any, id: string, forceRefresh: () => void, dispatch: Dispatch<DocumentDispatchType>) {
  try {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_FETCH, payload: { isLoading: true, values: undefined } });

    const resp = await api.documents.findOne(entryForm.id, id);

    dispatch({ type: DocumentActionsEnum.DOCUMENT_FETCH, payload: { isLoading: false, values: resp.data } });

    if (resp.data.fileUploadStatus === FileUploadStatusEnum.OK) {
      dispatch(validateEntryForm(entryForm) as any);
    }

    return resp;
  } catch (error) {
    dispatch({ type: DocumentActionsEnum.DOCUMENT_FETCH, payload: { isLoading: false, values: undefined } });

    if (_.get(error, 'response.status') === 403) {
      // file has been deleted, force table refresh to stop polling
      forceRefresh();
    }
  }
}

export async function fetchDocApproved(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_APPROVED_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocInco(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_INCORPORATED_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocAdditional(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_ADDITIONAL_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocEugbrAdditional(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_EUGBR_ADDITIONAL_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocEugbrs(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_EUGBR_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocEugbrComments(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_EUGBR_COMMENT_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export async function fetchDocCSSF(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_CSSF_FETCH, payload: { isLoading: false, values: data } });
}

export async function fetchDocTranslation(data: DocumentType[], dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_TRANSLATION_FETCH, payload: { isLoading: false, refreshIndex: 0, values: data } });
}

export function refreshDocTranslation(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_TRANSLATION_REFRESH });
}

export function refreshDocEugbrs(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_REFRESH });
}

export function refreshDocEugbrComments(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_COMMENT_REFRESH });
}

export function refreshDocEugbrAdditional(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_DOCUMENT_EUGBR_ADDITIONAL_REFRESH });
}

export function refreshDocApproved(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_APPROVED_REFRESH });
}

export function refreshDocIncorporated(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_INCORPORATED_REFRESH });
}

export function refreshDocAdditional(dispatch: Dispatch<DocumentDispatchType>) {
  dispatch({ type: DocumentActionsEnum.DOCUMENT_ADDITIONAL_REFRESH });
}

export async function download(entryFormId: string, id: string) {
  const response: any = await api.documents.download(entryFormId, id);

  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', response.headers['xx-filename']);
  document.body.appendChild(link);
  link.click();
}
